import React, { useState, useEffect, useMemo, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { db, auth } from '../firebase';
import {
  collection, onSnapshot, doc, addDoc, updateDoc, deleteDoc,
  serverTimestamp, query, where,
} from 'firebase/firestore';

// ---------------------------------------------------------------------------
// Grid Rank Tracker — owner-only internal SEO tool. Not client-facing.
// Tracks local map-rank across a geo-grid around a client's business, one
// keyword at a time, until it hits top-3. Reuses the client's existing local
// pack config (localPlaceId + localScanPoint set in the Content Data tab) as
// the business + grid center — no separate setup, no geocoding.
//
// Firestore rules restrict keywordTargets + gridScans to the owner account, so
// the initial read below doubles as the access gate (a denied read → locked).
// Weekly scans are written by the runGridScan/gridScanScheduler functions.
//
// Phil is color-blind, so the grid encodes rank as the NUMBER in each cell
// (primary signal) over a single-hue light→dark ramp — never a red/green
// heatmap — and deltas are stated in words ("better"/"worse"), not just color.
// ---------------------------------------------------------------------------

const RUN_SCAN_URL = 'https://us-central1-core6-marketing.cloudfunctions.net/runGridScan';
const RANK_CAP = 20; // 20 = "not in the top 20 / not found"
const GRID_SIZES = [7, 12];
// Per-point search radius. Higher z = tighter/hyper-local; lower = wider. Wider
// lets a distant service-area business register (at the cost of grid granularity).
const ZOOM_OPTIONS = [
  { z: 14, label: 'Hyper-local (14z)' },
  { z: 13, label: 'Local (13z)' },
  { z: 12, label: 'City-wide (12z)' },
  { z: 11, label: 'Regional (11z)' },
];
const zoomLabel = (z) => (ZOOM_OPTIONS.find((o) => o.z === z) || {}).label || `${z || 14}z`;

const one = (n) => (typeof n === 'number' ? Math.round(n * 10) / 10 : null);
const money = (n) => (typeof n === 'number' ? '$' + n.toFixed(n < 1 ? 3 : 2) : '—');

function fmtDate(ts) {
  const d = ts && typeof ts.toDate === 'function' ? ts.toDate() : (ts instanceof Date ? ts : null);
  if (!d) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
function fmtDateTime(ts) {
  const d = ts && typeof ts.toDate === 'function' ? ts.toDate() : (ts instanceof Date ? ts : null);
  if (!d) return '—';
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// Single-hue (blue) lightness ramp: dark = top of the pack, light = far down.
// Every cell shows the number, so the color is reinforcement, not the signal.
function rankStyle(rank) {
  if (rank == null) return { bg: 'var(--bg-tertiary)', fg: 'var(--text-secondary)', label: '×', title: 'Lookup failed (excluded from averages)' };
  if (rank <= 3) return { bg: '#12306e', fg: '#ffffff', label: String(rank), weight: 700, title: `Rank ${rank} — top 3` };
  if (rank <= 6) return { bg: '#2456c7', fg: '#ffffff', label: String(rank), title: `Rank ${rank}` };
  if (rank <= 10) return { bg: '#5b8def', fg: '#0b1220', label: String(rank), title: `Rank ${rank}` };
  if (rank < RANK_CAP) return { bg: '#b6d0f7', fg: '#0b1220', label: String(rank), title: `Rank ${rank}` };
  return { bg: '#e8eef9', fg: '#42506b', label: '20+', title: 'Not in the top 20' };
}

// Firestore forbids nested arrays, so the backend stores the grid as a row-major
// flat array of length size². Reshape it back to grid[r][c] for rendering.
function reshapeGrid(flat, size) {
  if (!Array.isArray(flat)) return null;
  if (Array.isArray(flat[0])) return flat; // already 2-D (defensive)
  const n = size || Math.round(Math.sqrt(flat.length));
  const rows = [];
  for (let r = 0; r < n; r++) rows.push(flat.slice(r * n, r * n + n));
  return rows;
}

// Recompute each grid point's true lat/lng from what the scan stores (center +
// size + spacing) — mirrors buildGridPoints server-side. Row 0 = north.
function gridPointLatLngs(centerLat, centerLng, size, spacingMiles) {
  const latDegPerMile = 1 / 69.0;
  const cos = Math.cos((centerLat * Math.PI) / 180) || 1;
  const lngDegPerMile = 1 / (69.0 * cos);
  const half = (size - 1) / 2;
  const pts = [];
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      pts.push({
        r, c,
        lat: centerLat + (half - r) * spacingMiles * latDegPerMile,
        lng: centerLng + (c - half) * spacingMiles * lngDegPerMile,
      });
    }
  }
  return pts;
}

// Leaflet DivIcon for a grid point: same rank-as-number + lightness ramp as the
// heatmap, with a drop shadow (and gold ring on top-3) so it reads on any map.
function markerIcon(rank) {
  const s = rankStyle(rank);
  const ring = rank != null && rank <= 3 ? '0 0 0 2px #f5c542, ' : '';
  return L.divIcon({
    className: '',
    html: `<div style="width:22px;height:22px;border-radius:5px;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:700;background:${s.bg};color:${s.fg};box-shadow:${ring}0 1px 3px rgba(0,0,0,0.55);">${s.label}</div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });
}

// ---------- Grid heatmap ----------
function GridHeatmap({ grid, size, cell = 40 }) {
  if (!grid || !grid.length) return <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>No grid data.</div>;
  const n = size || grid.length;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${n}, 1fr)`, gap: 3, width: '100%', maxWidth: n * cell }}>
      {grid.map((row, r) => row.map((rank, c) => {
        const s = rankStyle(rank);
        return (
          <div key={`${r}-${c}`} title={s.title}
            style={{
              aspectRatio: '1 / 1', display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: s.bg, color: s.fg, fontWeight: s.weight || 600, fontSize: 12,
              borderRadius: 4, userSelect: 'none',
              outline: rank != null && rank <= 3 ? '2px solid #f5c542' : 'none', outlineOffset: -2,
            }}>
            {s.label}
          </div>
        );
      }))}
    </div>
  );
}

function Legend() {
  const items = [
    { label: '1–3', ...rankStyle(2) },
    { label: '4–6', ...rankStyle(5) },
    { label: '7–10', ...rankStyle(9) },
    { label: '11–19', ...rankStyle(15) },
    { label: '20+', ...rankStyle(20) },
    { label: 'failed', ...rankStyle(null) },
  ];
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', fontSize: 12, color: 'var(--text-secondary)', marginTop: 8 }}>
      <span>Rank:</span>
      {items.map((it) => (
        <span key={it.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 16, height: 16, borderRadius: 3, background: it.bg, color: it.fg, fontSize: 9, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>{it.label === 'failed' ? '×' : ''}</span>
          {it.label}
        </span>
      ))}
      <span style={{ marginLeft: 4 }}>· darker = higher rank · gold ring = top 3</span>
    </div>
  );
}

// ---------- Trend chart (inline SVG, single line, direct labels) ----------
function TrendChart({ title, series, yMin, yMax, invert, unit = '', hint }) {
  const W = 600, H = 170, padL = 36, padR = 54, padT = 18, padB = 28;
  const present = series.map((s, i) => ({ ...s, i })).filter((s) => typeof s.value === 'number');
  const xOf = (i) => padL + (series.length <= 1 ? 0 : (i / (series.length - 1)) * (W - padL - padR));
  const yOf = (v) => {
    const t = (v - yMin) / ((yMax - yMin) || 1);
    return padT + (invert ? t : 1 - t) * (H - padT - padB);
  };
  const line = present.map((p) => `${xOf(p.i)},${yOf(p.value)}`).join(' ');
  const last = present[present.length - 1];
  return (
    <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 2 }}>
        <strong style={{ fontSize: 13 }}>{title}</strong>
        <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{hint}</span>
      </div>
      {present.length === 0 ? (
        <div style={{ color: 'var(--text-secondary)', fontSize: 13, padding: '20px 0' }}>No data yet.</div>
      ) : (
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', overflow: 'visible' }}>
          {/* baseline + top gridline */}
          <line x1={padL} y1={H - padB} x2={W - padR} y2={H - padB} stroke="var(--border)" strokeWidth="1" />
          <line x1={padL} y1={padT} x2={W - padR} y2={padT} stroke="var(--border)" strokeWidth="1" strokeDasharray="2 4" opacity="0.5" />
          {/* y-axis end labels */}
          <text x={padL - 6} y={yOf(invert ? yMin : yMax) + 4} textAnchor="end" fontSize="10" fill="var(--text-secondary)">{invert ? yMin : yMax}{unit}</text>
          <text x={padL - 6} y={yOf(invert ? yMax : yMin) + 4} textAnchor="end" fontSize="10" fill="var(--text-secondary)">{invert ? yMax : yMin}{unit}</text>
          {/* the line */}
          {present.length > 1 && <polyline points={line} fill="none" stroke="#5b8def" strokeWidth="2.5" />}
          {/* points + value labels */}
          {present.map((p) => (
            <g key={p.i}>
              <circle cx={xOf(p.i)} cy={yOf(p.value)} r="3.5" fill="#12306e" stroke="#ffffff" strokeWidth="1.5" />
              <text x={xOf(p.i)} y={yOf(p.value) - 8} textAnchor="middle" fontSize="10" fontWeight="600" fill="var(--text-primary)">{one(p.value)}{unit}</text>
            </g>
          ))}
          {/* x-axis first/last date */}
          <text x={padL} y={H - padB + 16} textAnchor="start" fontSize="10" fill="var(--text-secondary)">{series[0]?.label}</text>
          {series.length > 1 && <text x={W - padR} y={H - padB + 16} textAnchor="end" fontSize="10" fill="var(--text-secondary)">{series[series.length - 1]?.label}</text>}
          {/* last value callout */}
          {last && <text x={W - padR + 6} y={yOf(last.value) + 4} textAnchor="start" fontSize="11" fontWeight="700" fill="#12306e">{one(last.value)}{unit}</text>}
        </svg>
      )}
    </div>
  );
}

function Stat({ label, value, sub, accent }) {
  return (
    <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', minWidth: 92 }}>
      <div style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: accent || 'var(--text-primary)', lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{sub}</div>}
    </div>
  );
}

// Word-first delta (color is secondary, for the color-blind).
function Delta({ oldV, newV, lowerBetter, unit = '' }) {
  if (typeof oldV !== 'number' || typeof newV !== 'number') return <span style={{ color: 'var(--text-secondary)' }}>—</span>;
  const d = Math.round((newV - oldV) * 10) / 10;
  if (d === 0) return <span style={{ color: 'var(--text-secondary)' }}>±0 · same</span>;
  const better = lowerBetter ? d < 0 : d > 0;
  return (
    <span style={{ color: better ? 'var(--accent-green)' : 'var(--accent-orange)', fontWeight: 600 }}>
      {d > 0 ? '+' : ''}{d}{unit} {better ? '▲ better' : '▼ worse'}
    </span>
  );
}

// ---------- Main ----------
export default function GridTracker() {
  const [gate, setGate] = useState('loading'); // loading | denied | open
  const [targets, setTargets] = useState([]);
  const [clients, setClients] = useState([]);
  const [selClientId, setSelClientId] = useState('');
  const [selTargetId, setSelTargetId] = useState('');
  const [scans, setScans] = useState([]);
  const [cmpA, setCmpA] = useState('');
  const [cmpB, setCmpB] = useState('');

  const [newKeyword, setNewKeyword] = useState('');
  const [newSize, setNewSize] = useState(12);
  const [newZoom, setNewZoom] = useState(14);
  const [newNotes, setNewNotes] = useState('');
  const [nextMode, setNextMode] = useState(false); // "mark complete → next keyword" form open

  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [scanningIds, setScanningIds] = useState([]); // keywordTarget ids with a scan in flight
  const [showMap, setShowMap] = useState(true);
  const mapDivRef = useRef(null);

  // Gate + subscriptions. The keywordTargets read is owner-only, so its error
  // callback drives the denied screen; clients is staff-readable (owner is staff).
  useEffect(() => {
    const unsubT = onSnapshot(
      collection(db, 'keywordTargets'),
      (s) => { setTargets(s.docs.map((d) => ({ id: d.id, ...d.data() }))); setGate('open'); },
      () => setGate('denied'),
    );
    const unsubC = onSnapshot(collection(db, 'clients'),
      (s) => setClients(s.docs.map((d) => ({ id: d.id, ...d.data() }))), () => {});
    return () => { unsubT(); unsubC(); };
  }, []);

  // Scans for the selected target (equality-only query → no composite index;
  // sorted newest-first client-side).
  useEffect(() => {
    if (!selTargetId) { setScans([]); return; }
    const unsub = onSnapshot(
      query(collection(db, 'gridScans'), where('keywordTargetId', '==', selTargetId)),
      (s) => {
        const list = s.docs.map((d) => {
          const data = d.data();
          return { id: d.id, ...data, grid: reshapeGrid(data.grid, data.gridSize) };
        }).sort((a, b) => (b.scannedAt?.seconds || 0) - (a.scannedAt?.seconds || 0));
        setScans(list);
      }, () => setScans([]),
    );
    return unsub;
  }, [selTargetId]);

  const clientsById = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);
  const sortedClients = useMemo(
    () => [...clients].filter((c) => !c.isArchived).sort((a, b) => (a.businessName || '').localeCompare(b.businessName || '')),
    [clients]);

  const clientTargets = useMemo(
    () => targets.filter((t) => t.clientId === selClientId)
      .sort((a, b) => (b.startedAt?.seconds || 0) - (a.startedAt?.seconds || 0)),
    [targets, selClientId]);
  const activeTarget = useMemo(() => clientTargets.find((t) => t.status === 'active') || null, [clientTargets]);
  const selTarget = useMemo(() => targets.find((t) => t.id === selTargetId) || null, [targets, selTargetId]);
  const selClient = clientsById[selClientId];
  const gridReady = !!(selClient && selClient.localScanPoint && selClient.localPlaceId);

  // When client changes, jump to its active target (or most recent).
  useEffect(() => {
    if (!selClientId) { setSelTargetId(''); return; }
    const preferred = clientTargets.find((t) => t.status === 'active') || clientTargets[0];
    setSelTargetId(preferred ? preferred.id : '');
    setNextMode(false);
    // Intentionally only re-run when the client changes — not on every targets
    // snapshot update, so a manual pick of a completed target isn't overridden.
  }, [selClientId]);

  // Default the compare selectors to latest vs prior whenever scans change.
  useEffect(() => {
    setCmpA(scans[0]?.id || '');
    setCmpB(scans[1]?.id || '');
  }, [scans]);

  const scanById = (id) => scans.find((s) => s.id === id) || null;
  const latest = scans[0] || null;
  const totalCost = useMemo(() => scans.reduce((s, x) => s + (x.estimatedCost || 0), 0), [scans]);

  // Trend series oldest→newest.
  const trend = useMemo(() => [...scans].reverse().map((s) => ({ label: fmtDate(s.scannedAt), agr: s.agr, solv: s.solv })), [scans]);

  // Render the latest scan onto a Leaflet/OSM map — each grid point at its true
  // coordinate plus a pin at the scan center. Declared AFTER `latest` so its dep
  // array doesn't hit the temporal dead zone. Wrapped in try/catch so a Leaflet
  // failure can never blank the page. DivIcon/circleMarker only → no marker-image
  // path issues.
  useEffect(() => {
    if (!showMap || !latest || !mapDivRef.current) return;
    if (!isFinite(latest.centerLat) || !isFinite(latest.centerLng)) return;
    let map;
    try {
      const grid = latest.grid;
      const spacing = latest.gridSpacingMiles || 0.7;
      const pts = gridPointLatLngs(latest.centerLat, latest.centerLng, latest.gridSize, spacing);
      map = L.map(mapDivRef.current, { scrollWheelZoom: false });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19, attribution: '&copy; OpenStreetMap contributors',
      }).addTo(map);
      pts.forEach((p) => {
        const rank = grid && grid[p.r] ? grid[p.r][p.c] : null;
        L.marker([p.lat, p.lng], { icon: markerIcon(rank), interactive: false, keyboard: false }).addTo(map);
      });
      L.circleMarker([latest.centerLat, latest.centerLng], {
        radius: 6, color: '#000', weight: 2, fillColor: '#f5c542', fillOpacity: 1,
      }).addTo(map).bindTooltip('Scan center');
      map.fitBounds(L.latLngBounds(pts.map((p) => [p.lat, p.lng])), { padding: [24, 24] });
    } catch (err) {
      console.error('Grid map render failed:', err);
    }
    const t = map ? setTimeout(() => { try { map.invalidateSize(); } catch (e) {} }, 120) : null;
    return () => { if (t) clearTimeout(t); if (map) { try { map.remove(); } catch (e) {} } };
  }, [showMap, latest?.id, latest?.centerLat, latest?.centerLng, latest?.gridSize]);

  async function startTarget() {
    if (!selClient || !gridReady) { setMsg('Set this client’s Place ID + scan point in the Content Data tab first.'); return; }
    const kw = newKeyword.trim();
    if (!kw) { setMsg('Enter a keyword to track.'); return; }
    if (activeTarget) { setMsg('This client already has an active keyword. Mark it complete first.'); return; }
    const [lat, lng] = String(selClient.localScanPoint).split(',').map((x) => parseFloat(x.trim()));
    setBusy(true); setMsg('');
    try {
      const ref = await addDoc(collection(db, 'keywordTargets'), {
        clientId: selClientId, clientName: selClient.businessName || '',
        keyword: kw, gridSize: newSize === 7 ? 7 : 12, zoom: newZoom, status: 'active',
        startedAt: serverTimestamp(), completedAt: null, notes: newNotes.trim() || null,
        centerLat: lat, centerLng: lng, placeId: selClient.localPlaceId,
        lastAgr: null, lastAtgr: null, lastSolv: null, lastCapShare: null,
        lastScanAt: null, lastAnomaly: null,
      });
      setNewKeyword(''); setNewNotes(''); setSelTargetId(ref.id);
      setMsg('Keyword target created. Run a scan to capture the first grid.');
    } catch (e) { setMsg('Could not create target: ' + e.message); }
    setBusy(false);
  }

  async function completeAndNext() {
    if (!activeTarget) return;
    const kw = newKeyword.trim();
    setBusy(true); setMsg('');
    try {
      await updateDoc(doc(db, 'keywordTargets', activeTarget.id), { status: 'completed', completedAt: serverTimestamp() });
      if (kw) {
        const [lat, lng] = String(selClient.localScanPoint).split(',').map((x) => parseFloat(x.trim()));
        const ref = await addDoc(collection(db, 'keywordTargets'), {
          clientId: selClientId, clientName: selClient.businessName || '',
          keyword: kw, gridSize: newSize === 7 ? 7 : 12, zoom: newZoom, status: 'active',
          startedAt: serverTimestamp(), completedAt: null, notes: newNotes.trim() || null,
          centerLat: lat, centerLng: lng, placeId: selClient.localPlaceId,
          lastAgr: null, lastAtgr: null, lastSolv: null, lastCapShare: null,
          lastScanAt: null, lastAnomaly: null,
        });
        setSelTargetId(ref.id);
        setMsg(`Retired “${activeTarget.keyword}”. Now tracking “${kw}”.`);
      } else {
        setMsg(`Retired “${activeTarget.keyword}”.`);
      }
      setNewKeyword(''); setNewNotes(''); setNextMode(false);
    } catch (e) { setMsg('Could not update: ' + e.message); }
    setBusy(false);
  }

  // Fire-and-track per target: the scan runs server-side, so we don't block the
  // whole UI. `target` is captured so switching clients mid-scan is safe, and
  // several scans can run at once. Results land via the gridScans onSnapshot.
  async function runScanNow() {
    if (!selTarget || scanningIds.includes(selTarget.id)) return;
    const target = selTarget;
    setScanningIds((ids) => [...ids, target.id]);
    setMsg(`Scanning “${target.keyword}” (${target.clientName || 'client'})… switch clients and start another while it runs.`);
    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch(RUN_SCAN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ clientId: target.clientId, keywordTargetId: target.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setMsg(`✓ “${target.keyword}” done — AGR ${one(data.agr)}, SoLV ${data.solv}%${data.failedPoints ? `, ${data.failedPoints} failed` : ''} (${money(data.estimatedCost)}).`);
    } catch (e) {
      setMsg(`Scan failed for “${target.keyword}”: ${e.message}`);
    } finally {
      setScanningIds((ids) => ids.filter((id) => id !== target.id));
    }
  }

  async function deleteEmptyTarget() {
    if (!selTarget || scans.length) return;
    if (!window.confirm(`Delete the keyword target “${selTarget.keyword}”? (No scans recorded — safe to remove.)`)) return;
    setBusy(true); setMsg('');
    try {
      await deleteDoc(doc(db, 'keywordTargets', selTarget.id));
      setSelTargetId('');
      setMsg('Target deleted.');
    } catch (e) { setMsg('Could not delete: ' + e.message); }
    setBusy(false);
  }

  async function deleteScan(scanId) {
    if (!window.confirm('Delete this scan? It is permanently removed from the history.')) return;
    try {
      await deleteDoc(doc(db, 'gridScans', scanId));
      setMsg('Scan deleted.');
    } catch (e) { setMsg('Could not delete scan: ' + e.message); }
  }

  // ----- render -----
  if (gate === 'loading') {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading…</div>;
  }
  if (gate === 'denied') {
    return (
      <div style={{ maxWidth: 460, margin: '80px auto', textAlign: 'center', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 12, padding: 32 }}>
        <h2 style={{ marginTop: 0 }}>Grid Rank Tracker</h2>
        <p style={{ color: 'var(--text-secondary)' }}>This page is owner-only. Your account doesn’t have access.</p>
      </div>
    );
  }

  const anomalyText = selTarget?.lastAnomaly || latest?.anomaly?.reason || null;

  return (
    <div style={{ padding: '18px 20px', maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ marginBottom: 14 }}>
        <h1 style={{ margin: 0, fontSize: 22 }}>Grid Rank Tracker</h1>
        <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Internal · local map-rank across a geo-grid · weekly auto-scan (Mon 3 AM PT)</div>
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end', marginBottom: 14 }}>
        <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Client<br />
          <select value={selClientId} onChange={(e) => setSelClientId(e.target.value)}
            style={{ minWidth: 220, padding: '6px 8px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)' }}>
            <option value="">Select a client…</option>
            {sortedClients.map((c) => <option key={c.id} value={c.id}>{c.businessName}</option>)}
          </select>
        </label>

        {selClientId && (
          <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Keyword target<br />
            <select value={selTargetId} onChange={(e) => setSelTargetId(e.target.value)}
              style={{ minWidth: 260, padding: '6px 8px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)' }}>
              <option value="">Select…</option>
              {clientTargets.filter((t) => t.status === 'active').length > 0 && (
                <optgroup label="Active">
                  {clientTargets.filter((t) => t.status === 'active').map((t) => (
                    <option key={t.id} value={t.id}>{t.keyword} ({t.gridSize}×{t.gridSize})</option>
                  ))}
                </optgroup>
              )}
              {clientTargets.filter((t) => t.status === 'completed').length > 0 && (
                <optgroup label="Completed (history)">
                  {clientTargets.filter((t) => t.status === 'completed').map((t) => (
                    <option key={t.id} value={t.id}>✓ {t.keyword} ({t.gridSize}×{t.gridSize})</option>
                  ))}
                </optgroup>
              )}
            </select>
          </label>
        )}

        {selTarget && (
          <button className="btn btn-primary" disabled={scanningIds.includes(selTarget.id)} onClick={runScanNow}>
            {scanningIds.includes(selTarget.id) ? 'Scanning…' : 'Run scan now'}
          </button>
        )}
        {scanningIds.length > 0 && (
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            ⏳ {scanningIds.length} scan{scanningIds.length > 1 ? 's' : ''} running… (each updates here as it finishes)
          </span>
        )}
      </div>

      {msg && <div style={{ marginBottom: 12, padding: '8px 12px', background: 'var(--bg-tertiary)', borderRadius: 6, fontSize: 13 }}>{msg}</div>}

      {/* No client selected */}
      {!selClientId && <div style={{ color: 'var(--text-secondary)' }}>Pick a client to see its keyword target and scan history.</div>}

      {/* Client selected but not grid-ready */}
      {selClientId && !gridReady && (
        <div style={{ padding: 14, background: 'var(--bg-secondary)', border: '1px solid var(--accent-orange)', borderRadius: 8, fontSize: 13 }}>
          <strong>{selClient?.businessName}</strong> isn’t set up for grid scans yet. Set its <strong>Google Place ID</strong> and <strong>scan point (lat,lng)</strong> in the client’s <strong>Content Data</strong> tab — the grid tracker reuses those as the business and the grid center.
        </div>
      )}

      {/* Grid-ready, no active target → start form */}
      {selClientId && gridReady && !activeTarget && !selTarget && (
        <div style={{ padding: 14, background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8 }}>
          <h3 style={{ marginTop: 0 }}>Start tracking a keyword</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
            <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Keyword phrase<br />
              <input value={newKeyword} onChange={(e) => setNewKeyword(e.target.value)} placeholder="e.g. emergency plumber"
                style={{ width: 240, padding: '6px 8px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)' }} />
            </label>
            <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Grid size<br />
              <select value={newSize} onChange={(e) => setNewSize(Number(e.target.value))}
                style={{ padding: '6px 8px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)' }}>
                {GRID_SIZES.map((n) => <option key={n} value={n}>{n}×{n} ({n * n} points)</option>)}
              </select>
            </label>
            <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Search radius<br />
              <select value={newZoom} onChange={(e) => setNewZoom(Number(e.target.value))}
                style={{ padding: '6px 8px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)' }}>
                {ZOOM_OPTIONS.map((o) => <option key={o.z} value={o.z}>{o.label}</option>)}
              </select>
            </label>
            <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Notes (optional)<br />
              <input value={newNotes} onChange={(e) => setNewNotes(e.target.value)} placeholder="e.g. Phase 2 negatives live"
                style={{ width: 240, padding: '6px 8px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)' }} />
            </label>
            <button className="btn btn-success" disabled={busy || !newKeyword.trim()} onClick={startTarget}>Start</button>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 8 }}>Grid size and search radius lock for the life of this keyword (changing them mid-target would break comparability). Use a wider radius (City-wide/Regional) for a service-area client targeting a city it isn't physically in. Center: {selClient.localScanPoint}.</div>
        </div>
      )}

      {/* Selected target detail */}
      {selTarget && (
        <>
          {anomalyText && (
            <div style={{ margin: '4px 0 14px', padding: '10px 14px', background: 'rgba(245,197,66,0.14)', border: '1px solid #f5c542', borderRadius: 8, fontSize: 13 }}>
              <strong>⚠ Anomaly on the latest scan:</strong> {anomalyText}. Worth a look — could be a Google SERP layout shift or a real ranking drop.
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <div>
              <span style={{ fontSize: 17, fontWeight: 700 }}>{selTarget.keyword}</span>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)', marginLeft: 8 }}>
                {selTarget.gridSize}×{selTarget.gridSize} · {zoomLabel(selTarget.zoom || 14)} · started {fmtDate(selTarget.startedAt)}
                {selTarget.status === 'completed' && ` · ✓ completed ${fmtDate(selTarget.completedAt)}`}
                {selTarget.notes && ` · ${selTarget.notes}`}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {selTarget.status === 'active' && (
                <button className="btn btn-sm btn-secondary" disabled={busy} onClick={() => { setNextMode((v) => !v); setNewKeyword(''); setNewNotes(''); setNewSize(selTarget.gridSize); setNewZoom(selTarget.zoom || 14); }}>
                  {nextMode ? 'Cancel' : 'Mark top-3 achieved → next keyword'}
                </button>
              )}
              {scans.length === 0 && (
                <button className="btn btn-sm btn-secondary" disabled={busy} onClick={deleteEmptyTarget}>Delete</button>
              )}
            </div>
          </div>

          {/* Complete → next form */}
          {nextMode && selTarget.status === 'active' && (
            <div style={{ padding: 14, background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8, marginBottom: 14 }}>
              <div style={{ fontSize: 13, marginBottom: 8 }}>Retire <strong>“{selTarget.keyword}”</strong> and (optionally) start the next keyword for {selClient?.businessName}:</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Next keyword (leave blank to just retire)<br />
                  <input value={newKeyword} onChange={(e) => setNewKeyword(e.target.value)} placeholder="next phrase to chase"
                    style={{ width: 240, padding: '6px 8px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)' }} />
                </label>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Grid size<br />
                  <select value={newSize} onChange={(e) => setNewSize(Number(e.target.value))}
                    style={{ padding: '6px 8px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)' }}>
                    {GRID_SIZES.map((n) => <option key={n} value={n}>{n}×{n}</option>)}
                  </select>
                </label>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Search radius<br />
                  <select value={newZoom} onChange={(e) => setNewZoom(Number(e.target.value))}
                    style={{ padding: '6px 8px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)' }}>
                    {ZOOM_OPTIONS.map((o) => <option key={o.z} value={o.z}>{o.label}</option>)}
                  </select>
                </label>
                <button className="btn btn-success" disabled={busy} onClick={completeAndNext}>
                  {newKeyword.trim() ? 'Retire & start next' : 'Retire only'}
                </button>
              </div>
            </div>
          )}

          {scans.length === 0 ? (
            <div style={{ color: 'var(--text-secondary)', padding: '12px 0' }}>No scans yet. Click <strong>Run scan now</strong> to capture the first grid (or wait for Monday’s auto-scan).</div>
          ) : (
            <>
              {/* Latest metrics */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14, alignItems: 'stretch' }}>
                <Stat label="AGR" value={one(latest.agr) ?? '—'} sub="avg grid rank" accent="#5b8def" />
                <Stat label="ATGR" value={one(latest.atgr) ?? '—'} sub="avg where ranked" />
                <Stat label="SoLV" value={latest.solv != null ? latest.solv + '%' : '—'} sub="points in top 3" accent="#12306e" />
                <Stat label="Latest scan" value={fmtDate(latest.scannedAt)} sub={`${latest.gridSize}×${latest.gridSize} · ${latest.pointsTotal} pts`} />
                <Stat label="Cost" value={money(totalCost)} sub={`${scans.length} scan${scans.length > 1 ? 's' : ''} to date`} />
                {latest.incomplete && <Stat label="Incomplete" value={`${latest.failedPoints}`} sub="failed points" accent="var(--accent-orange)" />}
              </div>

              {/* Latest grid + legend */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Latest grid — {fmtDate(latest.scannedAt)}</div>
                <GridHeatmap grid={latest.grid} size={latest.gridSize} />
                <Legend />
              </div>

              {/* Map view — grid points at their true coordinates */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>Map view</span>
                  <button className="btn btn-sm btn-secondary" onClick={() => setShowMap((v) => !v)}>{showMap ? 'Hide map' : 'Show map'}</button>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    Center {Number(latest.centerLat).toFixed(5)}, {Number(latest.centerLng).toFixed(5)} · {latest.gridSize}×{latest.gridSize} · {latest.gridSpacingMiles || 0.7} mi spacing · {latest.zoom || 14}z · ~{Math.round((latest.gridSize - 1) * (latest.gridSpacingMiles || 0.7) * 10) / 10} mi across
                  </span>
                  <a href={`https://www.google.com/maps/@${latest.centerLat},${latest.centerLng},13z`} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#818cf8' }}>open in Google Maps ↗</a>
                </div>
                {showMap && <div ref={mapDivRef} style={{ height: 440, width: '100%', borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)' }} />}
              </div>

              {/* Trends */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12, marginBottom: 16 }}>
                <TrendChart title="Average Grid Rank (AGR)" hint="↑ = better (lower rank)" invert
                  yMin={1} yMax={RANK_CAP} series={trend.map((t) => ({ label: t.label, value: t.agr }))} />
                <TrendChart title="Share of Local Voice (SoLV)" hint="↑ = better" unit="%"
                  yMin={0} yMax={100} series={trend.map((t) => ({ label: t.label, value: t.solv }))} />
              </div>

              {/* Compare two scans */}
              {scans.length > 1 && (
                <div style={{ marginBottom: 16, padding: 14, background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Compare two scans</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
                    {[{ id: cmpA, set: setCmpA, tag: 'A' }, { id: cmpB, set: setCmpB, tag: 'B' }].map(({ id, set, tag }) => {
                      const s = scanById(id);
                      return (
                        <div key={tag} style={{ flex: '1 1 260px' }}>
                          <select value={id} onChange={(e) => set(e.target.value)}
                            style={{ width: '100%', padding: '6px 8px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)', marginBottom: 8 }}>
                            {scans.map((x) => <option key={x.id} value={x.id}>{fmtDateTime(x.scannedAt)} — AGR {one(x.agr) ?? '—'}, SoLV {x.solv ?? '—'}%</option>)}
                          </select>
                          {s ? <GridHeatmap grid={s.grid} size={s.gridSize} cell={30} /> : <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>—</div>}
                        </div>
                      );
                    })}
                  </div>
                  {/* Deltas B(newer/A older? show A→B) */}
                  {scanById(cmpA) && scanById(cmpB) && (
                    <div style={{ marginTop: 12, fontSize: 13 }}>
                      <div style={{ color: 'var(--text-secondary)', marginBottom: 4 }}>Change from B to A:</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18 }}>
                        <span>AGR: {one(scanById(cmpB).agr) ?? '—'} → {one(scanById(cmpA).agr) ?? '—'} &nbsp;<Delta oldV={scanById(cmpB).agr} newV={scanById(cmpA).agr} lowerBetter /></span>
                        <span>ATGR: {one(scanById(cmpB).atgr) ?? '—'} → {one(scanById(cmpA).atgr) ?? '—'} &nbsp;<Delta oldV={scanById(cmpB).atgr} newV={scanById(cmpA).atgr} lowerBetter /></span>
                        <span>SoLV: {scanById(cmpB).solv ?? '—'}% → {scanById(cmpA).solv ?? '—'}% &nbsp;<Delta oldV={scanById(cmpB).solv} newV={scanById(cmpA).solv} unit="%" /></span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* History table */}
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Scan history</div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ textAlign: 'left', color: 'var(--text-secondary)', borderBottom: '1px solid var(--border)' }}>
                        <th style={{ padding: '6px 8px' }}>Date</th>
                        <th style={{ padding: '6px 8px' }}>AGR</th>
                        <th style={{ padding: '6px 8px' }}>ATGR</th>
                        <th style={{ padding: '6px 8px' }}>SoLV</th>
                        <th style={{ padding: '6px 8px' }}>Points</th>
                        <th style={{ padding: '6px 8px' }}>Cost</th>
                        <th style={{ padding: '6px 8px' }}>Flags</th>
                        <th style={{ padding: '6px 8px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {scans.map((s) => (
                        <tr key={s.id} onClick={() => setCmpA(s.id)} title="Click to set as compare A"
                          style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer', background: s.id === cmpA ? 'rgba(91,141,239,0.12)' : 'transparent' }}>
                          <td style={{ padding: '6px 8px' }}>{fmtDateTime(s.scannedAt)}</td>
                          <td style={{ padding: '6px 8px', fontWeight: 600 }}>{one(s.agr) ?? '—'}</td>
                          <td style={{ padding: '6px 8px' }}>{one(s.atgr) ?? '—'}</td>
                          <td style={{ padding: '6px 8px' }}>{s.solv != null ? s.solv + '%' : '—'}</td>
                          <td style={{ padding: '6px 8px' }}>{s.pointsTotal}</td>
                          <td style={{ padding: '6px 8px' }}>{money(s.estimatedCost)}</td>
                          <td style={{ padding: '6px 8px' }}>
                            {s.incomplete && <span title={`${s.failedPoints} failed`} style={{ color: 'var(--accent-orange)' }}>incomplete </span>}
                            {s.anomaly?.flagged && <span title={s.anomaly.reason} style={{ color: '#b8860b' }}>⚠ anomaly</span>}
                          </td>
                          <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                            <button onClick={(e) => { e.stopPropagation(); deleteScan(s.id); }} title="Delete this scan"
                              style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 14, lineHeight: 1 }}>✕</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
